import React, { createContext, useState, useEffect } from "react";
import AuthService from "../../../../../app/base/services/authentication.service";
import { httpService } from "../../../../../app/base/services/httpService.service";
import PaymentModal from "./PaymentModal";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "../../../../../app/i18n/LanguageContext";

const NotificationContext = createContext();

const NotificationProvider = ({ children }) => {
   const { language } = useTranslation();
   const [notifications, setNotifications] = useState([]);
   const [notificationMetaData, setNotificationMetaData] = useState({});
   const [unreadCount, setUnreadCount] = useState(0);
   const [user, setUser] = useState(AuthService.getUser());
   const [prevNotificationCount, setPrevNotificationCount] = useState(0);
   const [pageNumber, setPageNumber] = useState(1);
   const [isModalVisible, setIsModalVisible] = useState(false);
   const [modalMessage, setModalMessage] = useState("");
   const location = useLocation();
   const [loadingMarkAllRead, setLoadingMarkAllRead] = useState(false);
   const [loadingMarkOneRead, setLoadingMarkOneRead] = useState({});
   const [toggleBell, setToggleBell] = useState(null);

   const setPage = (page) => {
      setPageNumber(page);
   };

   const getNotifications = async () => {
      try {
         await httpService.get(`/Notifications/all/${user?.id}?pageSize=10&pageNumber=1`, (res) => {
            setNotifications(res?.data);
            setNotificationMetaData(res?.metaData);
            setUnreadCount(res?.metaData?.unreadNotification);
         });
      } catch (error) {
         if (error?.response && error?.response?.data && error?.response?.data?.Errors) {
            const errorDetail = error?.response?.data?.Errors?.[0];

            if (
               errorDetail?.code === "invalid_subscription_member" &&
               location?.pathname !== "/admin/payment-information"
            ) {
               setModalMessage(errorDetail?.message);
               setIsModalVisible(true);
            }
         } else {
            console.error(error);
         }
      }
   };

   useEffect(() => {
      if (user) {
         // Re-fetch on language change too, so already-loaded notifications
         // (fetched under the previous X-Language) get replaced with ones
         // localized in the newly selected language.
         getNotifications();
      } else {
         setNotifications([]);
         setUnreadCount(0);
         setPrevNotificationCount(0);
      }
   }, [user, language.code]);

   const handleNewNotifications = () => {
      getNotifications();
   };

   const markNotificationAsRead = async (notificationId) => {
      try {
         setLoadingMarkOneRead((prev) => ({
            ...prev,
            [notificationId]: true
         }));
         await httpService.post("/Notifications/read", { id: notificationId });
         setNotifications((prevNotifications) =>
            prevNotifications?.map((notification) =>
               notification?.id === notificationId
                  ? { ...notification, isRead: true }
                  : notification
            )
         );
         setUnreadCount((prevCount) => Math.max(0, prevCount - 1));
      } catch (error) {
         console.error(error);
      } finally {
         setLoadingMarkOneRead((prev) => ({
            ...prev,
            [notificationId]: false
         }));
      }
   };

   const markAllNotificationsAsRead = async () => {
      try {
         setLoadingMarkAllRead(true);
         await httpService.post("/Notifications/read/all", { userId: user.id });
         setNotifications((prevNotifications) =>
            prevNotifications?.map((notification) => ({ ...notification, isRead: true }))
         );
         setUnreadCount(0);
      } catch (error) {
         console.log(error);
      } finally {
         setLoadingMarkAllRead(false);
      }
   };

   const setPopoverUnreadCount = () => {
      setUnreadCount((prevCount) => Math.max(0, prevCount - 1));
   };

   const setPopoverUnreadCountAll = () => {
      setUnreadCount(0);
   };

   const setPopoverIncCount = () => {
      setUnreadCount((prevCount) => Math.max(0, prevCount + 1));
   };

   function handleToggleBell(event) {
      setToggleBell(event.currentTarget);
   }

   function handleCloseBell() {
      setToggleBell(null);
   }

   const value = {
      notifications,
      notificationMetaData,
      user,
      unreadCount,
      markNotificationAsRead,
      markAllNotificationsAsRead,
      setUser,
      pageNumber,
      setPage,
      loadingMarkAllRead,
      loadingMarkOneRead,
      setPopoverUnreadCount,
      setPopoverUnreadCountAll,
      setPopoverIncCount,
      toggleBell,
      setToggleBell,
      handleToggleBell,
      handleCloseBell
   };

   return (
      <>
         <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
         <PaymentModal
            isVisible={isModalVisible}
            message={modalMessage}
            onClose={() => setIsModalVisible(false)}
         />
      </>
   );
};

export { NotificationContext, NotificationProvider };
